import { customFetch } from "@/client/custom-fetch";

import {
  TSaleDetailsResponse,
  TSaleReportFilters,
  TSalesReportResponse,
} from "./reports.types";

const API_BASE = "/sales";

function buildReportFiltersPayload(
  filters: Omit<TSaleReportFilters, "page" | "limit">
): Record<string, unknown> {
  const filtersObj: Record<string, unknown> = {};

  if (filters.sale_id) filtersObj.sale_id = filters.sale_id;
  if (filters.status) filtersObj.status = filters.status;
  if (filters.partner) filtersObj.partner = filters.partner;
  if (filters.customer_name) filtersObj.customer_name = filters.customer_name;
  if (filters.customer_cpf) filtersObj.customer_cpf = filters.customer_cpf;
  if (filters.insurance_company_id) {
    filtersObj.insurance_company_id = filters.insurance_company_id;
  }
  if (filters.product_id) filtersObj.product_id = filters.product_id;

  if (filters.value_sale) {
    filtersObj.value_sale = {};
    if (filters.value_sale.min) {
      (filtersObj.value_sale as Record<string, number>).min =
        filters.value_sale.min;
    }
    if (filters.value_sale.max) {
      (filtersObj.value_sale as Record<string, number>).max =
        filters.value_sale.max;
    }
  }

  if (filters.sale_date) {
    filtersObj.sale_date = {};
    if (filters.sale_date.start_date) {
      (filtersObj.sale_date as Record<string, string>).start_date =
        filters.sale_date.start_date;
    }
    if (filters.sale_date.end_date) {
      (filtersObj.sale_date as Record<string, string>).end_date =
        filters.sale_date.end_date;
    }
  }

  return filtersObj;
}

export const reportsApi = {
  getSalesReport: (filters: TSaleReportFilters): Promise<TSalesReportResponse> => {
    const filtersObj = buildReportFiltersPayload(filters);
    const paginationObj = {
      page: filters.page || 1,
      limit: filters.limit || 20,
    };

    const queryParams = new URLSearchParams();
    queryParams.append("filters", JSON.stringify(filtersObj));
    queryParams.append("pagination", JSON.stringify(paginationObj));

    const url = `${API_BASE}/report?${queryParams.toString()}`;

    return customFetch<TSalesReportResponse>(url, {
      method: "GET",
    });
  },

  exportSalesReport: (
    filters: Omit<TSaleReportFilters, "page" | "limit">
  ): Promise<string> => {
    const filtersObj = buildReportFiltersPayload(filters);
    const queryParams = new URLSearchParams();
    queryParams.append("filters", JSON.stringify(filtersObj));

    const url = `${API_BASE}/report/export?${queryParams.toString()}`;

    return customFetch<string>(url, {
      method: "GET",
    });
  },

  getSaleDetails: (
    transactionSearchId: string
  ): Promise<TSaleDetailsResponse> => {
    return customFetch<TSaleDetailsResponse>(
      `${API_BASE}/report/${transactionSearchId}`,
      {
        method: "GET",
      }
    );
  },

  getPendingSales: (): Promise<any[]> => {
    return customFetch<any[]>(`${API_BASE}/pending`, {
      method: "GET",
    });
  },

  integrateSale: (
    externalId: string,
    infinisisSaleId: number
  ): Promise<void> => {
    return customFetch<void>(`${API_BASE}/integrate/${externalId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ infinisis_sale_id: infinisisSaleId }),
    });
  },

  markIntegrationFailed: (
    externalId: string,
    errors: Array<{ error_code: number; error_message: string }>
  ): Promise<void> => {
    return customFetch<void>(
      `${API_BASE}/integration-failed/${externalId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ errors }),
      }
    );
  },
};
